import {
  BarChart2,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Eye,
  Filter,
  History,
  MoreVertical,
  Search,
} from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { formatDateTime, formatNumber } from '@/lib/format';
import type {
  AudienceClass,
  BroadcastDateRange,
  BroadcastRecipientKind,
  BroadcastStatus,
  BroadcastSummary,
  MessageChannel,
  MessageTemplate,
} from '@/types/messaging';

export interface HistoryFilters {
  dateRange: BroadcastDateRange;
  channel: MessageChannel | 'ALL';
  templateKey: string;
  status: BroadcastStatus | 'ALL';
  includes: BroadcastRecipientKind | 'ALL';
  sectionUuid: string;
  search: string;
}

interface NotificationHistoryCardProps {
  filters: HistoryFilters;
  setFilter: <K extends keyof HistoryFilters>(key: K, value: HistoryFilters[K]) => void;
  clearFilters: () => void;
  history: BroadcastSummary[];
  total: number;
  page: number;
  limit: number;
  setPage: (page: number) => void;
  isLoading: boolean;
  error: ApiRequestError | null;
  onRetry: () => void;
  onSelectNotification: (item: BroadcastSummary) => void;
  /** For the section filter — the same list the compose card's picker uses. */
  classes: AudienceClass[];
  templates: MessageTemplate[];
}

/** How a send's status reads in the table, and which tone it carries. */
const STATUS_LABEL: Record<BroadcastStatus, { label: string; tone: 'success' | 'critical' | 'warning' }> = {
  SENT: { label: 'Sent', tone: 'success' },
  PARTIAL: { label: 'Partly sent', tone: 'warning' },
  FAILED: { label: 'Failed', tone: 'critical' },
};

/**
 * The page numbers to draw, with an ellipsis where a run is skipped.
 *
 * The design shows `1 2 3 … 6`; this produces the same shape for any number of
 * pages rather than the fixed five the mock drew, and collapses to just the
 * pages that exist when there are few.
 */
function pageNumbers(current: number, totalPages: number): (number | 'gap')[] {
  if (totalPages <= 6) return Array.from({ length: totalPages }, (_, i) => i + 1);

  const pages = new Set<number>([1, totalPages, current, current - 1, current + 1]);
  const shown = [...pages].filter((page) => page >= 1 && page <= totalPages).sort((a, b) => a - b);

  const out: (number | 'gap')[] = [];
  let previous = 0;

  for (const page of shown) {
    if (previous && page - previous > 1) out.push('gap');
    out.push(page);
    previous = page;
  }

  return out;
}

export function NotificationHistoryCard({
  filters,
  setFilter,
  clearFilters,
  history,
  total,
  page,
  limit,
  setPage,
  isLoading,
  error,
  onRetry,
  onSelectNotification,
  classes,
  templates,
}: NotificationHistoryCardProps) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const firstOnPage = total === 0 ? 0 : (page - 1) * limit + 1;
  const lastOnPage = Math.min(page * limit, total);

  /** Every section the institute runs, flattened for the one filter dropdown. */
  const sectionOptions = classes.flatMap((item) =>
    item.sections.map((section) => ({
      value: section.uuid,
      label: `${item.name} - ${section.name}`,
    })),
  );

  async function copyMessage(row: BroadcastSummary) {
    try {
      await navigator.clipboard.writeText(row.message);
      toast.success('Copied', `The text of "${row.title}" is on your clipboard.`);
    } catch {
      // Clipboard access is refused outside a secure context and in some
      // embedded browsers. Saying so beats a success toast that did nothing.
      toast.error('Could not copy', 'Your browser would not let the page use the clipboard.');
    }
  }

  return (
    <div className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-800 p-4 sm:p-6 shadow-xs flex flex-col gap-5 w-full max-w-full overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-900/60 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
            <History className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-700 dark:text-white">
              Notification History
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5">
              View all your sent broadcast notifications and delivery logs.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            disabled
            title="Exporting the history is not available yet"
            className="text-[13px] sm:text-[14px] font-extrabold border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0b1322] text-slate-800 dark:text-slate-100 hover:bg-slate-50 shadow-xs transition-all cursor-pointer py-1.5 sm:py-2 px-3 sm:px-3.5 rounded-xl flex items-center gap-2"
          >
            <Download className="h-4.5 w-4.5" />
            <span>Export</span>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled
            title="Bulk actions are not available yet"
            className="text-[13px] sm:text-[14px] font-extrabold border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0b1322] text-slate-800 dark:text-slate-100 hover:bg-slate-50 shadow-xs transition-all cursor-pointer py-1.5 sm:py-2 px-3 sm:px-3.5 rounded-xl"
          >
            <span>Bulk actions</span>
          </Button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex items-center gap-2 p-2.5 rounded-2xl bg-slate-50 dark:bg-[#0b1322] border border-slate-200/90 dark:border-slate-800 shadow-xs overflow-x-auto table-scrollbar flex-nowrap w-full max-w-full">
        {/* Date Range Dropdown */}
        <div className="w-[225px] shrink-0">
          <Select
            label="Date range filter"
            hideLabel
            className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            options={[
              { value: 'ALL', label: 'All time' },
              { value: 'LAST_7_DAYS', label: 'Last 7 Days' },
              { value: 'LAST_30_DAYS', label: 'Last 30 Days' },
              { value: 'THIS_MONTH', label: 'This Month' },
              { value: 'PREVIOUS_MONTH', label: 'Previous Month' },
            ]}
            value={filters.dateRange}
            onChange={(e) => setFilter('dateRange', e.target.value as BroadcastDateRange)}
          />
        </div>

        <div className="w-[130px] shrink-0">
          <Select
            label="Channel filter"
            hideLabel
            className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            options={[
              { value: 'ALL', label: 'All channels' },
              { value: 'SMS', label: 'SMS' },
              { value: 'WHATSAPP', label: 'WhatsApp' },
            ]}
            value={filters.channel}
            onChange={(e) => setFilter('channel', e.target.value as MessageChannel | 'ALL')}
          />
        </div>

        <div className="w-[125px] shrink-0">
          <Select
            label="Type filter"
            hideLabel
            minPanelWidth={220}
            className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            options={[
              { value: 'ALL', label: 'All types' },
              ...templates.map((item) => ({ value: item.key, label: item.name })),
            ]}
            value={filters.templateKey}
            onChange={(e) => setFilter('templateKey', e.target.value)}
          />
        </div>

        <div className="w-[125px] shrink-0">
          <Select
            label="Status filter"
            hideLabel
            className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            options={[
              { value: 'ALL', label: 'All statuses' },
              { value: 'SENT', label: 'Sent' },
              { value: 'PARTIAL', label: 'Partly sent' },
              { value: 'FAILED', label: 'Failed' },
            ]}
            value={filters.status}
            onChange={(e) => setFilter('status', e.target.value as BroadcastStatus | 'ALL')}
          />
        </div>

        <div className="w-[135px] shrink-0">
          <Select
            label="Audience filter"
            hideLabel
            className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            options={[
              { value: 'ALL', label: 'All audiences' },
              { value: 'PARENT', label: 'Parents' },
              { value: 'STUDENT', label: 'Students' },
              { value: 'TEACHER', label: 'Teachers' },
              { value: 'STAFF', label: 'Staff' },
            ]}
            value={filters.includes}
            onChange={(e) => setFilter('includes', e.target.value as BroadcastRecipientKind | 'ALL')}
          />
        </div>

        <div className="w-[125px] shrink-0">
          <Select
            label="Section filter"
            hideLabel
            className="!h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
            options={[{ value: 'ALL', label: 'All sections' }, ...sectionOptions]}
            value={filters.sectionUuid}
            onChange={(e) => setFilter('sectionUuid', e.target.value)}
          />
        </div>

        <div className="flex-1 min-w-[180px]">
          <Input
            label="Search message or recipients"
            hideLabel
            placeholder="Search message or recipients..."
            value={filters.search}
            onChange={(e) => setFilter('search', e.target.value)}
            className="!h-9 !py-1 !pl-9 !pr-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !border-slate-200 dark:!border-slate-700/80 !bg-white dark:!bg-[#111c2e] shadow-2xs hover:!border-indigo-300 transition-colors"
            leftIcon={<Search className="h-3.5 w-3.5 text-slate-400" />}
          />
        </div>

        <Button
          variant="secondary"
          size="sm"
          className="!h-9 !py-1 !px-3 !text-[13px] !font-medium border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-200 hover:bg-slate-50 hover:text-indigo-600 hover:border-indigo-300 shadow-2xs cursor-pointer rounded-xl flex items-center gap-1.5 shrink-0 transition-colors"
          onClick={clearFilters}
        >
          <Filter className="h-3.5 w-3.5 text-indigo-500" />
          <span>Clear</span>
        </Button>
      </div>

      {/* History Table Container */}
      <div className="relative overflow-x-auto table-scrollbar rounded-2xl border border-slate-200 dark:border-slate-800">
        <table className="w-full text-left border-collapse min-w-[850px]">
          <thead>
            <tr className="bg-slate-100 dark:bg-[#0b1322] border-b border-slate-200 dark:border-slate-800 text-[14px] font-bold tracking-wide text-slate-700 dark:text-slate-300">
              <th className="py-3.5 px-4 sm:sticky sm:left-0 z-20 bg-slate-100 dark:bg-[#0b1322] sm:border-r border-slate-200 dark:border-slate-800 sm:shadow-2xs">
                Date &amp; Time
              </th>
              <th className="py-3.5 px-4">Message</th>
              <th className="py-3.5 px-4">Channel</th>
              <th className="py-3.5 px-4">Audience</th>
              <th className="py-3.5 px-4">Recipients</th>
              <th className="py-3.5 px-4">Status</th>
              <th className="py-3.5 px-4">Delivery</th>
              <th className="py-3.5 px-4 text-right sm:sticky sm:right-0 z-20 bg-slate-100 dark:bg-[#0b1322] sm:border-l border-slate-200 dark:border-slate-800 sm:shadow-2xs">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200/80 dark:divide-slate-800/80 text-sm">
            {history.map((row) => {
              const status = STATUS_LABEL[row.status];
              const percentage =
                row.recipientsCount > 0
                  ? `${Math.round((row.deliveredCount / row.recipientsCount) * 100)}%`
                  : '—';

              return (
                <tr
                  key={row.uuid}
                  className="hover:bg-slate-50/80 dark:hover:bg-slate-900/40 transition-colors"
                >
                  {/* STICKY FIXED DATE & TIME CELL */}
                  <td className="sm:sticky sm:left-0 z-10 bg-white dark:bg-[#111c2e] py-3.5 px-5 font-black text-slate-700 dark:text-slate-200 whitespace-nowrap text-[12px] sm:border-r border-slate-200/60 dark:border-slate-800/60 sm:shadow-2xs">
                    {formatDateTime(row.sentAt)}
                  </td>

                  {/* MIDDLE SCROLLABLE CELLS */}
                  <td className="py-2.5 px-5 max-w-sm">
                    <button
                      type="button"
                      onClick={() => onSelectNotification(row)}
                      className="font-black text-slate-700 dark:text-white block text-[13px] tracking-wide text-left hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer transition-colors"
                    >
                      {row.title}
                    </button>
                    <span className="text-slate-500 dark:text-slate-400 text-[12px] truncate block mt-0.5">
                      {row.message}
                    </span>
                  </td>
                  <td className="py-2.5 px-5 whitespace-nowrap">
                    <Badge tone={row.channel === 'SMS' ? 'accent' : 'success'}>
                      {row.channel === 'SMS' ? 'SMS' : 'WhatsApp'}
                    </Badge>
                  </td>
                  <td className="py-2.5 px-5 font-bold text-[12px] text-slate-700 dark:text-slate-200 whitespace-nowrap">
                    {row.audienceLabel}
                  </td>
                  <td className="py-2.5 px-5 font-black text-slate-700 text-[12px] dark:text-white whitespace-nowrap">
                    {formatNumber(row.recipientsCount)}
                  </td>
                  <td className="py-2.5 px-5 whitespace-nowrap">
                    <Badge tone={status.tone}>{status.label}</Badge>
                  </td>
                  <td className="py-2.5 px-5 font-black whitespace-nowrap text-[12px]">
                    <span className="text-slate-700 dark:text-white">
                      {formatNumber(row.deliveredCount)}/{formatNumber(row.recipientsCount)}
                    </span>
                    <span className="text-slate-600 font-normal ml-1.5">({percentage})</span>
                  </td>

                  {/* STICKY FIXED ACTIONS CELL */}
                  <td className="sm:sticky sm:right-0 z-10 bg-white dark:bg-[#111c2e] py-2.5 px-5 text-right whitespace-nowrap sm:border-l border-slate-200/60 dark:border-slate-800/60 sm:shadow-2xs">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        title="View log"
                        onClick={() => onSelectNotification(row)}
                        className="p-1.5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 transition-colors cursor-pointer rounded-lg border-0 bg-transparent"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title="Copy message"
                        onClick={() => void copyMessage(row)}
                        className="p-1.5 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/60 transition-colors cursor-pointer rounded-lg border-0 bg-transparent"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title="Delivery breakdown"
                        onClick={() => onSelectNotification(row)}
                        className="p-1.5 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/60 transition-colors cursor-pointer rounded-lg border-0 bg-transparent"
                      >
                        <BarChart2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title="More options are not available yet"
                        disabled
                        className="p-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer rounded-lg border-0 bg-transparent disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {isLoading && history.length === 0 && (
              <tr>
                <td colSpan={8} className="py-6 px-5">
                  <SkeletonLines count={4} />
                </td>
              </tr>
            )}

            {error && !isLoading && (
              <tr>
                <td colSpan={8} className="py-10 text-center">
                  <p className="text-sm font-bold text-slate-600 dark:text-slate-300">
                    {error.message}
                  </p>
                  <button
                    type="button"
                    onClick={onRetry}
                    className="mt-2 text-sm font-extrabold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                  >
                    Try again
                  </button>
                </td>
              </tr>
            )}

            {!isLoading && !error && history.length === 0 && (
              <tr>
                <td colSpan={8} className="py-10 text-center text-slate-400 font-medium">
                  {total === 0 && !filters.search && filters.dateRange === 'ALL'
                    ? 'Nothing has been sent yet. Your first broadcast will appear here.'
                    : 'No notification history found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Table Pagination Footer */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500 dark:text-slate-400 font-medium pt-2">
        <span>
          Showing {firstOnPage} to {lastOnPage} of {total} entries
        </span>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Previous</span>
          </Button>

          {/* Numbered Page Buttons */}
          {pageNumbers(page, totalPages).map((entry, index) =>
            entry === 'gap' ? (
              <span key={`gap-${index}`} className="px-1 text-slate-400 font-bold">
                …
              </span>
            ) : (
              <button
                key={entry}
                type="button"
                onClick={() => setPage(entry)}
                aria-current={entry === page ? 'page' : undefined}
                className={
                  entry === page
                    ? 'h-8 w-8 rounded-lg bg-indigo-600 text-white font-black text-xs flex items-center justify-center shadow-xs cursor-pointer'
                    : 'h-8 w-8 rounded-lg bg-white dark:bg-[#111c2e] border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-extrabold text-xs flex items-center justify-center hover:bg-slate-50 transition-colors cursor-pointer'
                }
              >
                {entry}
              </button>
            ),
          )}

          <Button
            variant="secondary"
            size="sm"
            className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            <span>Next</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
